import { Plus, FolderPlus, UserPlus, Upload, Images } from 'lucide-react';
import { OrbitIcon } from "@/components/icons/OrbitIcon";
import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ManualEntryModal } from '@/components/crm/ManualEntryModal';
import CreateProjectModal from '@/components/projects/CreateProjectModal';
// POL-2 — same NewGalleryDialog the Galleries page uses. Mounting it
// here lets the studio launch the new-gallery flow straight from
// Today without hopping to the Galleries tab first.
import { NewGalleryDialog } from '@/components/gallery/NewGalleryDialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCreateClient, ClientCapReachedError } from '@/services/clients';
import { useOrganization } from '@/services/organizations';
import { useInquiryCategories } from '@/services/inquiryCategories';
import { useToast } from '@/hooks/use-toast';
// HOTFIX-4 — "Organization not found" toasts carry a Set up action.
import { ToastAction } from '@/components/ui/toast';
import { useNavigate } from 'react-router-dom';

interface QuickActionsProps {
  onRefresh?: () => void;
}

export function QuickActions({ onRefresh }: QuickActionsProps) {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [projectModalOpen, setProjectModalOpen] = useState(false);
  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  // POL-2 — mounts the same NewGalleryDialog Galleries.tsx uses.
  const [newGalleryOpen, setNewGalleryOpen] = useState(false);
  
  // Client form state
  const [clientName, setClientName] = useState('');
  const [clientPhone, setClientPhone] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientBrand, setClientBrand] = useState('');
  const [clientCategoryId, setClientCategoryId] = useState('');
  
  const createClient = useCreateClient();
  const { data: organization } = useOrganization();
  const { data: categories } = useInquiryCategories();
  const { toast } = useToast();
  const navigate = useNavigate();

  const handleCreateClient = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientName.trim()) {
      toast({
        title: 'Name required',
        description: 'Please enter a client name.',
        variant: 'destructive',
      });
      return;
    }

    if (!organization?.id) {
      toast({
        title: 'Your studio isn’t set up yet',
        description: 'Please complete your studio setup first.',
        variant: 'destructive',
        // HOTFIX-4 — don't dead-end: take them to setup.
        action: (
          <ToastAction
            altText="Complete your studio setup"
            onClick={() => navigate('/onboarding')}
          >
            Set up
          </ToastAction>
        ),
      });
      return;
    }

    try {
      await createClient.mutateAsync({
        name: clientName.trim(),
        phone: clientPhone.trim() || null,
        email: clientEmail.trim() || null,
        brand: clientBrand.trim() || null,
        category_id: clientCategoryId || null,
        org_id: organization.id,
      });
      
      toast({
        title: 'Client created',
        description: `${clientName} has been added successfully.`,
      });
      
      // Reset form and close modal
      setClientName('');
      setClientPhone('');
      setClientEmail('');
      setClientBrand('');
      setClientCategoryId('');
      setClientModalOpen(false);
      onRefresh?.();
    } catch (error) {
      // PLANS-1B — Starter tier is capped at 15 clients. QuickActions
      // uses a lightweight inline form (no room for the full
      // UpgradeNudgeCard), so we close the modal + toast the news and
      // hand the studio to the Clients page where their next "Add
      // client" tap renders the inline nudge inside the dialog.
      if (error instanceof ClientCapReachedError) {
        setClientModalOpen(false);
        toast({
          title: `You've filled your ${error.cap}-client roster.`,
          description: 'Move up to Orbit for unlimited clients — Settings → Billing.',
        });
        navigate('/clients');
        return;
      }
      console.error('Failed to create client:', error);
      toast({
        title: "That didn't save",
        description: 'The client didn\'t land. Give it another try.',
        variant: 'destructive',
      });
    }
  };

  // Sentence case throughout per Mission Control discipline
  // (ORBIT_DESIGN.md §3.3) — actions are statements, not titles.
  const actions = [
    {
      icon: Plus,
      label: 'New inquiry',
      onClick: () => setInquiryModalOpen(true),
    },
    {
      icon: FolderPlus,
      label: 'New project',
      onClick: () => setProjectModalOpen(true),
    },
    {
      icon: UserPlus,
      label: 'Add client',
      onClick: () => setClientModalOpen(true),
    },
    {
      // POL-2 — opens the same NewGalleryDialog the Galleries tab uses
      // (pick photos → Nova project-match → create + upload atomically).
      icon: Images,
      label: 'New gallery',
      onClick: () => setNewGalleryOpen(true),
    },
    {
      icon: Upload,
      label: 'Upload files',
      onClick: () => setUploadModalOpen(true),
    },
  ];

  return (
    <>
      <div className="flex flex-wrap gap-2.5 stagger-children">
        {actions.map((action) => (
          <button
            key={action.label}
            type="button"
            onClick={action.onClick}
            className="quick-action-btn"
          >
            <span className="quick-action-icon">
              <action.icon className="w-4 h-4" />
            </span>
            <span className="quick-action-label">{action.label}</span>
          </button>
        ))}
      </div>

      {/* Inquiry Modal */}
      <Dialog open={inquiryModalOpen} onOpenChange={setInquiryModalOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <ManualEntryModal onClose={() => setInquiryModalOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Project Modal */}
      <CreateProjectModal
        open={projectModalOpen}
        onOpenChange={(open) => {
          setProjectModalOpen(open);
          if (!open) onRefresh?.();
        }}
      />

      {/* Client Modal */}
      <Dialog open={clientModalOpen} onOpenChange={setClientModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-editorial text-2xl leading-none">Add a client</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateClient} className="space-y-4">
            <div>
              <Label htmlFor="clientName">Client name *</Label>
              <Input
                id="clientName"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Who's this for?"
                className="mt-1"
                required
              />
            </div>
            <div>
              <Label htmlFor="clientPhone">Phone Number</Label>
              <Input
                id="clientPhone"
                type="tel"
                value={clientPhone}
                onChange={(e) => setClientPhone(e.target.value)}
                placeholder="(555) 123-4567"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="clientEmail">Email</Label>
              <Input
                id="clientEmail"
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="client@example.com"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="clientBrand">Brand</Label>
              <Input
                id="clientBrand"
                value={clientBrand}
                onChange={(e) => setClientBrand(e.target.value)}
                placeholder="Brand name"
                className="mt-1"
              />
            </div>
            <div>
              <Label htmlFor="clientCategory">Category / Inquiry Type</Label>
              <Select value={clientCategoryId} onValueChange={setClientCategoryId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories?.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>
                      {cat.name.trim()}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setClientModalOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={createClient.isPending}>
                {createClient.isPending ? 'Creating...' : 'Add Client'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* POL-2 — new-gallery flow reused from the Galleries page. */}
      <NewGalleryDialog
        open={newGalleryOpen}
        onClose={() => setNewGalleryOpen(false)}
      />

      {/* Upload Modal */}
      <Dialog open={uploadModalOpen} onOpenChange={setUploadModalOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
          </DialogHeader>
          <div className="py-8 text-center">
            <OrbitIcon name="square.and.arrow.up" fallback={Upload} className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">
              To upload files, please open a specific project first.
            </p>
            <p className="text-sm text-muted-foreground">
              Project files can be uploaded from the Project Workspace → Uploads tab.
            </p>
          </div>
          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setUploadModalOpen(false)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
